import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Link, Navigate } from '../components/nav'
import { useEmbedded } from '../lib/embed'
import { useExperimentalFlag } from '../hooks/useExperimentalFlag'
import { useGetProjectQuery, useGetProjectMembersQuery } from '../services/projectDataApi'
import ProjectGithubChatCard from '../components/chat/ProjectGithubChatCard'
import '../components/chat/chat.css'
import SelfHealingSessions from '../components/SelfHealingSessions'
import ContinuousTestingSetup from '../components/ContinuousTestingSetup'
import AgentConnectionSetup from '../components/AgentConnectionSetup'
import '../components/TestingSetup.css'
import './ProjectSupport.css'

const titles: Record<string, string> = {
  'self-healing': 'Self Healing',
  'test-schedule': 'Test Schedule',
  security: 'Security',
  'agent-connection': 'Agent Connection',
}

export default function ProjectSupport() {
  const { projectId = '', support = '' } = useParams()
  const [githubOpen, setGithubOpen] = useState(false)
  const embedded = useEmbedded()
  const flag = useExperimentalFlag('continuous_testing')
  const query = useGetProjectQuery(projectId, { pollingInterval: 15000, skipPollingIfUnfocused: true })
  const members = useGetProjectMembersQuery(projectId)
  const role = members.currentData?.role
  const canEdit = role === 'owner' || role === 'admin' || role === 'member'
  const project = query.currentData
  if (support === 'security') return <Navigate to={`/projects/${projectId}/security`} replace />
  if (!flag.loaded) return <p role="status">Loading…</p>
  if (flag.enabled && support === 'continuous-testing') return <Navigate to={`/projects/${projectId}/support/test-schedule`} replace />
  if (!flag.enabled || !Object.hasOwn(titles, support)) return <Navigate to={`/projects/${projectId}/overview`} replace />
  return <div className="project-support" data-testid="project-support-page">
    <header className="project-support__header">
    <Link to={`/projects/${projectId}/overview`}>← Overview</Link>
    <h1>{titles[support]}</h1>
    {support === 'test-schedule' && <p>Choose how often to test your app, how much to cover, and what to focus on.</p>}
    </header>
    <div className={`project-support__content${support === 'test-schedule' ? ' testing-settings' : ''}`}>
    {query.isLoading && <p role="status">Loading project…</p>}
    {query.isError && <p role="alert">Could not load project. <button onClick={() => void query.refetch()}>Retry</button></p>}
    {project && <>
      {support === 'self-healing' && <SelfHealingSessions key={projectId} projectId={projectId} />}
      {support === 'test-schedule' && <>
        <ContinuousTestingSetup key={projectId} projectId={projectId} canEdit={canEdit} showDescription={false} />
        <section className="project-support__github" aria-labelledby="github-connection-title">
          <div>
            <h2 id="github-connection-title">GitHub connection</h2>
            <p>{project.github?.repoUrl ? 'Connected' : 'Not connected'}</p>
            {project.github?.repoUrl && <p className="project-support__repository">{project.github.repoUrl}</p>}
            <p>Connect your repository to test pull requests and main-branch deployments.</p>
          </div>
          {canEdit && (embedded ? <a href={`/projects/${projectId}/support/test-schedule`} target="_blank" rel="noopener noreferrer">Open GitHub setup in the full app</a> : <button type="button" onClick={() => {
            setGithubOpen(true)
          }}>{project.github?.repoUrl ? 'Manage GitHub connection' : 'Setup GitHub connection'}</button>)}
        </section>
        {canEdit && githubOpen && <ProjectGithubChatCard key={projectId} project={project} onClose={() => setGithubOpen(false)} />}
      </>}
      {support === 'agent-connection' && <AgentConnectionSetup project={project} canEdit={canEdit} />}
    </>}
    </div>
  </div>
}
